import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker } from "react-router-dom";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { NAVIGATION_INTENT_EVENT } from "./navigationGuard";

const DEFAULT_MESSAGE = "You have unpublished changes. Leave this page and discard them?";
const UnsavedWorkContext = createContext(null);

function guardMessage(entries) {
  if (entries.length === 1) return entries[0].message || DEFAULT_MESSAGE;
  return "You have unsaved changes in more than one open editor. Leaving now will discard all of them.";
}

function activeEntries(registry) {
  return [...registry.current.values()];
}

/**
 * The application uses a data router, so this boundary can protect every
 * React Router transition (links, navigate(), Back and Forward) with the
 * existing accessible, focus-trapped confirmation dialog. It mounts a router
 * blocker only while shared guarded work is dirty; this avoids competing with
 * specialised route blockers on unrelated screens.
 */
function ActiveRouterBlocker({ registry, revision, onBlockedChange }) {
  const allowNextNavigationRef = useRef(false);
  // Whether the operator has actually been ASKED about the currently-blocked navigation. This is
  // the difference between "the work vanished before we prompted" (a save completing in the same
  // task) and "the work vanished while the operator was deciding" (they undid the edit themselves)
  // — the two need opposite resolutions, and only this flag can tell them apart.
  const promptedRef = useRef(false);
  // A decision has already been issued for the CURRENTLY blocked navigation. The effect below
  // re-runs whenever the registry changes, and every resolution path mutates the registry
  // (`onDiscard` clears the editor), so without this latch the effect re-enters after a decision
  // and issues a SECOND, contradictory one — confirming "Discard and leave" ran onDiscard, emptied
  // the registry, and the re-entry then read that as "nothing to confirm" and RESET the very
  // navigation the operator had just approved.
  const resolvedRef = useRef(false);
  const [confirmation, setConfirmation] = useState(null);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowNextNavigationRef.current) {
      allowNextNavigationRef.current = false;
      return false;
    }
    // This blocker stays mounted for one extra commit while a blocked navigation is resolved, so
    // it can outlive the guarded work itself. Nothing dirty means nothing to confirm.
    if (!activeEntries(registry).length) return false;
    return `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`
      !== `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;
  });

  // Hold this component mounted until the router has resolved a blocked navigation. Unmounting
  // while blocked deletes the blocker and strands the navigation: React Router never resumes it.
  const blocked = blocker.state === "blocked";
  useEffect(() => {
    onBlockedChange?.(blocked);
    return () => onBlockedChange?.(false);
  }, [blocked, onBlockedChange]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    const onNavigationIntent = (event) => {
      const entries = activeEntries(registry);
      if (!entries.length) return;
      const accepted = typeof window.confirm === "function"
        && window.confirm(guardMessage(entries));
      if (!accepted) {
        event.preventDefault();
        return;
      }
      try {
        entries.forEach((entry) => entry.onDiscard?.());
      } catch {
        event.preventDefault();
        return;
      }
      // requestInAppNavigation is synchronous. Let the navigate()/setSearchParams call made in
      // the same task pass the data-router blocker, then restore protection immediately.
      allowNextNavigationRef.current = true;
      Promise.resolve().then(() => {
        allowNextNavigationRef.current = false;
      });
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener(NAVIGATION_INTENT_EVENT, onNavigationIntent);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener(NAVIGATION_INTENT_EVENT, onNavigationIntent);
    };
  }, [registry, revision]);

  useEffect(() => {
    if (blocker.state !== "blocked") {
      promptedRef.current = false;
      resolvedRef.current = false;
      return;
    }
    if (resolvedRef.current) return;
    const entries = activeEntries(registry);
    if (!entries.length) {
      // Nothing left to confirm. HOW that happened decides what to do, and the two cases need
      // OPPOSITE resolutions:
      //
      // (a) We never prompted. The work was saved in the same task that requested this navigation
      //     — a successful save closes its editor and deep-links to the record it just created,
      //     and react-router evaluates this blocker SYNCHRONOUSLY inside navigate(), before React
      //     has committed that close. Proceed: dropping it silently lost the deep link and left
      //     the operator on the list with no created record open.
      //
      // (b) We already prompted, and the work disappeared while the operator was deciding —
      //     typically because they answered by UNDOING the edit rather than by using the dialog.
      //     Proceeding here would resolve their own question for them and navigate away without
      //     consent (it closed the record drawer out from under them). Cancel instead: the
      //     operator stays exactly where they are, which is what reverting an edit implies.
      setConfirmation(null);
      resolvedRef.current = true;
      if (promptedRef.current) {
        promptedRef.current = false;
        blocker.reset();
      } else {
        blocker.proceed();
      }
      return;
    }
    promptedRef.current = true;
    setConfirmation({
      title: "Discard unsaved changes?",
      body: guardMessage(entries),
      danger: true,
      confirmLabel: "Discard and leave",
      cancelLabel: "Keep editing",
      confirmTestId: "unsaved-work-discard",
      onConfirm: () => {
        // Latch BEFORE running the callbacks: `onDiscard` empties the registry synchronously, and
        // the resulting effect re-entry would otherwise reset this accepted navigation.
        resolvedRef.current = true;
        // Run discard callbacks and proceed in the same task. A callback may synchronously clear
        // its dirty state; yielding before proceed would unmount this blocker and strand the
        // accepted navigation.
        const discardResults = entries.map((entry) => entry.onDiscard?.());
        blocker.proceed();
        return Promise.all(discardResults);
      },
    });
  }, [blocker, registry, revision]);

  const closeConfirmation = useCallback(() => {
    if (blocker.state === "blocked") {
      resolvedRef.current = true;
      blocker.reset();
    }
    activeEntries(registry).forEach((entry) => entry.onRestore?.());
    setConfirmation(null);
  }, [blocker, registry]);

  return <ConfirmDialog state={confirmation} onClose={closeConfirmation} />;
}

/**
 * Mount once below RouterProvider. Consumers continue to use
 * useUnsavedWorkGuard and remain testable without a data router; the boundary
 * upgrades them to full SPA route protection in the real application.
 */
export function UnsavedWorkGuardBoundary({ children }) {
  const registry = useRef(new Map());
  const [revision, setRevision] = useState(0);
  const register = useCallback((id, entry) => {
    registry.current.set(id, entry);
    setRevision((current) => current + 1);
    return () => {
      if (!registry.current.delete(id)) return;
      setRevision((current) => current + 1);
    };
  }, []);
  const context = useMemo(() => ({ register }), [register]);
  const [resolvingNavigation, setResolvingNavigation] = useState(false);
  const hasDirtyWork = registry.current.size > 0;

  return (
    <UnsavedWorkContext.Provider value={context}>
      {children}
      {(hasDirtyWork || resolvingNavigation) && (
        <ActiveRouterBlocker
          registry={registry}
          revision={revision}
          onBlockedChange={setResolvingNavigation}
        />
      )}
    </UnsavedWorkContext.Provider>
  );
}

/**
 * Protects dirty work from hard exits and from navigation routed through the
 * app's cancelable navigation-intent contract. The browser controls the copy
 * shown for beforeunload; in-app exits use the explicit supplied message.
 */
export function useUnsavedWorkGuard(dirty, {
  message = DEFAULT_MESSAGE,
  onDiscard,
  onRestore,
} = {}) {
  const boundary = useContext(UnsavedWorkContext);
  const guardId = useId();
  const dirtyRef = useRef(!!dirty);
  const discardRef = useRef(onDiscard);
  const restoreRef = useRef(onRestore);
  const restoringHistoryRef = useRef(false);
  const [pendingDiscard, setPendingDiscard] = useState(null);
  dirtyRef.current = !!dirty;
  discardRef.current = onDiscard;
  restoreRef.current = onRestore;

  const confirmDiscard = useCallback(() => {
    if (!dirtyRef.current) return true;
    if (typeof window === "undefined" || typeof window.confirm !== "function") return false;
    return window.confirm(message);
  }, [message]);

  useEffect(() => {
    if (!dirty || !boundary) return undefined;
    return boundary.register(guardId, {
      message,
      onDiscard: () => discardRef.current?.(),
      onRestore: () => restoreRef.current?.(),
    });
  }, [boundary, dirty, guardId, message]);

  useEffect(() => {
    if (!dirty || boundary) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    const onNavigationIntent = (event) => {
      if (!confirmDiscard()) {
        event.preventDefault();
        return;
      }
      discardRef.current?.();
    };
    const onPopState = () => {
      if (restoringHistoryRef.current) {
        restoringHistoryRef.current = false;
        return;
      }
      if (confirmDiscard()) {
        discardRef.current?.();
        return;
      }
      // popstate fires after the browser moves. Returning one entry forward restores the guarded
      // URL without fabricating another history entry; the next popstate is the restoration event.
      restoreRef.current?.();
      restoringHistoryRef.current = true;
      window.history.go(1);
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener(NAVIGATION_INTENT_EVENT, onNavigationIntent);
    // Capture runs before BrowserRouter's bubble-phase history listener, while the editor's dirty
    // snapshot and discard callback still belong to the route being left.
    window.addEventListener("popstate", onPopState, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener(NAVIGATION_INTENT_EVENT, onNavigationIntent);
      window.removeEventListener("popstate", onPopState, true);
    };
  }, [boundary, confirmDiscard, dirty]);

  const requestDiscard = useCallback((action) => {
    if (!dirtyRef.current) {
      action?.();
      return true;
    }
    setPendingDiscard(() => action || (() => {}));
    return false;
  }, []);

  const cancelDiscard = useCallback(() => {
    restoreRef.current?.();
    setPendingDiscard(null);
  }, []);

  const discardDialogState = useMemo(() => pendingDiscard ? {
    title: "Discard unsaved changes?",
    body: message,
    danger: true,
    confirmLabel: "Discard changes",
    cancelLabel: "Keep editing",
    confirmTestId: "unsaved-work-discard",
    onConfirm: async () => {
      // Invoke both callbacks before yielding so a synchronous state reset cannot unmount the
      // editor before its accepted close action runs.
      const discardResult = discardRef.current?.();
      const actionResult = pendingDiscard();
      await Promise.all([discardResult, actionResult]);
    },
  } : null, [message, pendingDiscard]);

  return {
    confirmDiscard,
    requestDiscard,
    discardDialogState,
    cancelDiscard,
  };
}

/**
 * Accessible local-exit confirmation for drawers, dialogs and tab-local
 * editors. Route exits are handled by UnsavedWorkGuardBoundary.
 */
export function UnsavedWorkGuardDialog({ guard }) {
  return (
    <ConfirmDialog
      state={guard?.discardDialogState}
      onClose={guard?.cancelDiscard}
    />
  );
}
